import { computed, unref } from 'vue';
import { UNCATEGORIZED_ID, UNCATEGORIZED_LABEL } from '@/constants/transactionCategoryKinds';
import { roundMoney } from '@/composables/usePaymentMethodCalculations';

export function useResultadoReport(transactionsRef, categoriesRef) {
  const transactions = computed(() => unref(transactionsRef) || []);
  const categories = computed(() => unref(categoriesRef) || []);

  const categoryMap = computed(() =>
    Object.fromEntries(categories.value.map((c) => [c.id, c]))
  );

  const incomeCategories = computed(() =>
    categories.value.filter((c) => c.kind === 'income')
  );

  const expenseCategories = computed(() =>
    categories.value.filter((c) => c.kind === 'expense')
  );

  function buildRows(kind, categoryList) {
    const totals = {};
    for (const cat of categoryList) {
      totals[cat.id] = 0;
    }
    totals[UNCATEGORIZED_ID] = 0;

    for (const tx of transactions.value) {
      const amount = Number(tx.amount) || 0;
      const isIncome = amount > 0;
      const isExpense = amount < 0;
      if (kind === 'income' && !isIncome) continue;
      if (kind === 'expense' && !isExpense) continue;

      const key = tx.categoryId || UNCATEGORIZED_ID;
      if (key !== UNCATEGORIZED_ID && !totals[key]) {
        totals[key] = 0;
      }
      totals[key] = roundMoney(totals[key] + (kind === 'expense' ? Math.abs(amount) : amount));
    }

    const rows = categoryList.map((cat) => ({
      id: cat.id,
      name: cat.name,
      amount: totals[cat.id] || 0,
    }));

    rows.push({
      id: UNCATEGORIZED_ID,
      name: UNCATEGORIZED_LABEL,
      amount: totals[UNCATEGORIZED_ID] || 0,
    });

    for (const [id, amount] of Object.entries(totals)) {
      if (id === UNCATEGORIZED_ID) continue;
      if (categoryList.some((c) => c.id === id)) continue;
      const cat = categoryMap.value[id];
      if (cat && cat.kind === kind) {
        rows.push({ id, name: cat.name, amount });
      }
    }

    return rows.filter((r) => r.amount !== 0);
  }

  const incomeRows = computed(() => buildRows('income', incomeCategories.value));
  const expenseRows = computed(() => buildRows('expense', expenseCategories.value));

  const totalIncome = computed(() =>
    roundMoney(incomeRows.value.reduce((sum, r) => sum + r.amount, 0))
  );

  const totalExpense = computed(() =>
    roundMoney(expenseRows.value.reduce((sum, r) => sum + r.amount, 0))
  );

  const netResult = computed(() => roundMoney(totalIncome.value - totalExpense.value));

  return {
    incomeRows,
    expenseRows,
    totalIncome,
    totalExpense,
    netResult,
  };
}
